import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import toolClient from '@requests/telnyx/tools.requests';

import { BuildOutlined, Close, WarningAmberRounded } from '@mui/icons-material';
import { ToolIcon } from '../AssistantTool';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Alert, AlertTitle, Chip, Divider, IconButton, Stack, Tab } from '@mui/material';
import { TabContainer } from '@common/Node/CompletionChatbot';
import { useTheme } from '../../../../../../../styles';
import { LoadingButton } from '@common/Button';
import { Footer } from '../../Assistant';
import { AssistantToolConfig } from './AssistantToolConfig/AssistantToolConfig';
import { NodeAIArgument } from '@components/Flow/Nodes';
import { AssistantToolExecution } from './AssistantToolExecution/AssistantToolExecution';
import { AssistantToolArguments } from './AssistantToolArguments/AssistantToolArguments';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RequestError } from '@requests/common';
import { AIToolCreate } from '@requests/telnyx';
import { ToolSnackbarOrigin } from '../AssistantTools';
import { AssistantToolSettings } from './AssistantToolSettings/AssistantToolSettings';
import { getToolDefault } from '../tool.defaults';
import { AssistantToolCopy } from './AssistantToolCopy/AssistantToolCopy';

import {
  AITool,
  AIToolEvent,
  AIToolMetadata,
  ToolDrawerOpen,
  ToolExecution,
  ToolModification,
  ToolType,
} from '../tool.types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;

  & .MuiTabPanel-root {
    padding: 0;
    flex: 1;
    min-width: 0;
    height: 100%;
  }

  #tool-drawer-title {
    color: ${({ theme }) => theme.text.standard};
    font-family: 'PPFormula';
    font-size: 16px;
    padding-top: 4px;
    font-weight: 700;
    max-width: 360px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const HeaderTabs = styled.div`
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.background.standard};
  z-index: 100;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem 1.25rem;
  width: calc(100% - 2.5rem);
  gap: 1rem;
`;

const TabContent = styled.div`
  display: flex;
  width: calc(100% - 40px);
  padding: 16px 20px;
`;

export const getExecutionOptions = (type: ToolType) => {
  if (type === ToolType.FLOW) {
    return [
      { label: 'Bucket Search', value: ToolExecution.BUCKET_SEARCH },
      { label: 'Static Document', value: ToolExecution.STATIC_DOCUMENT },
    ];
  }

  if (type === ToolType.VOICE) {
    return [
      { label: 'Bucket Retrieval', value: ToolExecution.BUCKET_RETRIEVAL },
      { label: 'Hangup Call', value: ToolExecution.HANGUP_CALL },
    ];
  }

  return [];
};

interface Props {
  openTool: ToolDrawerOpen;
  setOpenTool: React.Dispatch<React.SetStateAction<ToolDrawerOpen | null>>;
  setChangesMade: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AssistantToolDrawer: React.FC<Props> = ({ openTool, setOpenTool, setChangesMade }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { tool, modification } = openTool;
  const { enqueueSnackbar } = useSnackbar();
  const { name, description } = getToolDefault(openTool.tool.execution);

  const [tab, setTab] = useState('1');
  const [toolName, setToolName] = useState(tool.name);
  const [toolDescription, setToolDescription] = useState(tool.description);
  const [toolInstructions, setToolInstructions] = useState(tool.instructions);
  const [toolMetadata, setToolMetadata] = useState<AIToolMetadata>(tool.metadata);
  const [toolArguments, setToolArguments] = useState<NodeAIArgument[]>(openTool.tool.arguments);
  const [toolChoice, setToolChoice] = useState<string>(openTool.tool.settings.tool_choice);
  const [toolEvents, setToolEvents] = useState<AIToolEvent[]>(openTool.tool.settings.events);

  const toolConfig = {
    toolName,
    setToolName,
    toolDescription,
    setToolDescription,
    toolInstructions,
    setToolInstructions,
    toolMetadata,
    setToolMetadata,
  };

  const toolExecutionConfig = {
    toolMetadata,
    setToolMetadata,
    toolArguments,
  };

  const toolArgsConfig = { toolArguments, setToolArguments };
  const toolSettingsConfig = { toolChoice, setToolChoice, toolEvents, setToolEvents, toolArguments };

  const updatedTool: AITool = {
    ...tool,
    assistant_id: tool.assistant_id,
    name: toolName,
    description: toolDescription,
    instructions: toolInstructions,
    execution: tool.execution,
    type: tool.type,
    metadata: toolMetadata,
    arguments: toolArguments,
    settings: {
      events: toolEvents,
      tool_choice: toolChoice,
    },
  };

  const existingTool = {
    ...tool,
    description: tool.description,
    metadata: tool.metadata,
    name: tool.name,
    settings: {
      events: tool.settings.events,
      tool_choice: tool.settings.tool_choice,
    },
  };

  const toolHasChanges = JSON.stringify(existingTool) !== JSON.stringify(updatedTool);

  // updates state outside component to enable/disable closing drawer by clicking away
  useEffect(() => setChangesMade(toolHasChanges), [toolHasChanges]);

  const { mutateAsync: create, isPending: creatingTool } = useMutation<void, RequestError, AIToolCreate>({
    mutationFn: toolClient.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_assistants'] });
      queryClient.invalidateQueries({ queryKey: ['ai_assistants_tools'] });
      enqueueSnackbar('Tool created successfully!', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: ToolSnackbarOrigin,
      });
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Failed to create tool!', {
        variant: 'error',
        autoHideDuration: 10000,
        anchorOrigin: ToolSnackbarOrigin,
      });

      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 10000,
        anchorOrigin: ToolSnackbarOrigin,
      });
    },
  });

  const { mutateAsync: edit, isPending: updatingTool } = useMutation<string | undefined, RequestError, AIToolCreate>({
    mutationFn: toolClient.update,
    onSuccess: (tool_id) => {
      queryClient.invalidateQueries({ queryKey: ['ai_assistants'] });
      queryClient.invalidateQueries({ queryKey: ['ai_assistants_tools'] });
      enqueueSnackbar('Tool updated successfully!', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: ToolSnackbarOrigin,
      });
      setOpenTool({ tool: { ...updatedTool, id: tool_id || updatedTool.id }, modification: ToolModification.EDIT });
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Failed to update tool!', {
        variant: 'error',
        autoHideDuration: 10000,
        anchorOrigin: ToolSnackbarOrigin,
      });

      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 10000,
        anchorOrigin: ToolSnackbarOrigin,
      });
    },
  });

  const modifyTool = async (modification: ToolModification) => {
    try {
      if (modification === ToolModification.CREATE) {
        await create({ assistantId: tool.assistant_id, tool: updatedTool });
        setOpenTool(null);
      }

      if (modification === ToolModification.EDIT) await edit({ assistantId: tool.assistant_id, tool: updatedTool });
    } catch {}
  };

  const handleToolCopied = (copiedTool: AITool) => {
    const newTool: AITool = {
      ...tool,
      name: copiedTool.name,
      description: copiedTool.description,
      instructions: copiedTool.instructions,
      execution: copiedTool.execution,
      metadata: { ...copiedTool.metadata } as AIToolMetadata,
      arguments: [...copiedTool.arguments],
      settings: { ...copiedTool.settings },
    };

    setToolName(copiedTool.name);
    setToolDescription(copiedTool.description);
    setToolInstructions(copiedTool.instructions);
    setToolMetadata(copiedTool.metadata);
    setToolArguments([...copiedTool.arguments]);
    setToolChoice(copiedTool.settings.tool_choice);
    setToolEvents([...copiedTool.settings.events]);

    setOpenTool({
      tool: newTool,
      modification: ToolModification.CREATE,
      isCopyMode: false,
    });
  };

  return (
    <Container>
      <TabContext value={tab}>
        <HeaderTabs>
          <HeaderContainer>
            <Stack direction='row' justifyContent='space-between' pt={1}>
              <Stack direction='row' spacing={2} alignItems='center'>
                <ToolIcon>
                  <BuildOutlined />
                </ToolIcon>
                {toolName ? <h1 id='tool-drawer-title'>{toolName}</h1> : <h1 id='tool-drawer-title'>Untitled Tool</h1>}
              </Stack>
              <Stack direction='row' spacing={1} alignItems='center'>
                <Chip
                  label={modification === ToolModification.CREATE ? 'Add Tool' : 'Edit Tool'}
                  size='small'
                  sx={{ maxWidth: '160px' }}
                />
                <IconButton onClick={() => setOpenTool(null)}>
                  <Close />
                </IconButton>
              </Stack>
            </Stack>
          </HeaderContainer>
          <TabContainer color={theme.primary}>
            <TabList onChange={(_, newValue) => setTab(newValue)}>
              <Tab disableRipple label='Tool' value='1' />
              {tool.type === ToolType.FLOW && !openTool.isCopyMode && <Tab disableRipple label='Arguments' value='2' />}
              {tool.type === ToolType.FLOW && !openTool.isCopyMode && <Tab disableRipple label='Execution' value='3' />}
              {tool.type === ToolType.FLOW && !openTool.isCopyMode && <Tab disableRipple label='Settings' value='4' />}
            </TabList>
          </TabContainer>
        </HeaderTabs>
        <TabPanel value='1'>
          <TabContent>
            {openTool.isCopyMode ? (
              <AssistantToolCopy onToolSelected={handleToolCopied} />
            ) : (
              <AssistantToolConfig openTool={openTool} toolConfig={toolConfig} />
            )}
          </TabContent>
        </TabPanel>
        <TabPanel value='2'>
          <TabContent>
            <AssistantToolArguments toolArgsConfig={toolArgsConfig} />
          </TabContent>
        </TabPanel>
        <TabPanel value='3'>
          <Stack spacing={1}>
            <Stack sx={{ padding: '16px 20px 8px 20px' }}>
              <Alert severity='info'>
                <AlertTitle>{name}</AlertTitle>
                {description}
              </Alert>
            </Stack>
            <Divider />
            <TabContent>
              <AssistantToolExecution openTool={openTool} toolExecutionConfig={toolExecutionConfig} />
            </TabContent>
          </Stack>
        </TabPanel>
        <TabPanel value='4'>
          <TabContent>
            <AssistantToolSettings openTool={openTool} toolSettingsConfig={toolSettingsConfig} />
          </TabContent>
        </TabPanel>
      </TabContext>
      {(modification === ToolModification.CREATE || (modification === ToolModification.EDIT && toolHasChanges)) &&
        !openTool.isCopyMode && (
          <Footer margin={16}>
            <Stack spacing={1} direction='row' alignItems='center'>
              <WarningAmberRounded sx={{ color: theme.text.standard }} />
              <p>
                {modification === ToolModification.EDIT ? 'You have unsaved changes.' : 'This tool is not saved yet.'}
              </p>
            </Stack>
            <Stack spacing={1} direction='row'>
              {modification === ToolModification.EDIT && (
                <LoadingButton
                  customColor='black'
                  minHeight='32px'
                  minWidth='123px'
                  loading={updatingTool}
                  onClick={() => modifyTool(ToolModification.EDIT)}
                >
                  Save Changes
                </LoadingButton>
              )}
              {modification === ToolModification.CREATE && (
                <LoadingButton
                  customColor='black'
                  minWidth='95px'
                  minHeight='32px'
                  loading={creatingTool}
                  onClick={() => modifyTool(ToolModification.CREATE)}
                >
                  Add Tool
                </LoadingButton>
              )}
            </Stack>
          </Footer>
        )}
    </Container>
  );
};
